// src/pages/TriagePage.js
//
// Auth-aware triage flow.
//
// Both anonymous and authenticated visitors now land directly on the
// form — the local "intro" screen this page used to have was removed
// once StartPage.js ('/') became the real front door (see
// portal_start_page_plan.md). Clicking "Start Snapshot" on StartPage.js's
// Snapshot card routes straight here, straight to the form.
//
// Anonymous mode:
//   form → /spainsnapshot/results/:token
//   Results page shows email capture / magic link.
//
// Authenticated mode:
//   form → claim-session fires automatically (via claimSession.js) → /spainsnapshot/results/:token
//   No email capture screen. Result page shows "Saved ✓" + hub CTA.
//
// Claim path: claim-session (product: 'triage') is the single claim function
// for Snapshot sessions. The old triage-claim function was retired in 049.

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useProductMode } from '../hooks/useProductMode'
import { useTriageForm, STEP_CONFIG } from '../hooks/useTriageForm'
import { supabase } from '../lib/supabase'
import { fetchPartnerNameByRef } from '../lib/partnerLookup'
import { claimSession } from '../lib/claimSession'
import { getSlotOptions } from '../lib/slotConfig'
import { AnonHeader, PartnerIntroScreen } from '../components/HomeLink'
import posthog from '../lib/posthog'
import { getActivePartnerRef } from '../lib/partnerAttribution'
import { validateEmail } from '../lib/emailValidation'
// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

const COMPLEXITY_META = {
  simple:   { cls: 'simple',   label: 'Simple',          desc: 'Your Spain-side tax situation appears relatively straightforward. Standard filings likely apply.' },
  moderate: { cls: 'moderate', label: 'Moderate',        desc: 'There are meaningful Spain tax considerations here. A gestor can help you navigate these correctly.' },
  complex:  { cls: 'complex',  label: 'Complex',         desc: 'Your situation involves several significant Spain tax factors. Professional guidance is recommended.' },
  high:     { cls: 'high',     label: 'High complexity', desc: 'Your situation involves multiple high-complexity Spain tax factors. Professional guidance is strongly recommended.' },
}

const FLAG_META = {
  high:   { cls: 'sev-high',   label: 'High' },
  medium: { cls: 'sev-medium', label: 'Attention' },
  info:   { cls: 'sev-info',   label: 'Note' },
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// "Not sure?" inline explainer — reveals stepCfg.helpText without it being
// a stored answer value. Mirrors the calculator's ELIGIBILITY_HELP_TEXT
// pattern exactly: a third stored "not sure" option was tried in triage's
// Beckham question before and dropped, because it never changed any flag
// logic beyond "active or not" — this keeps that same lesson from
// reappearing for this or any future Yes/No field.
function HelpToggle({ text }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="triage-help-toggle">
      <button className="triage-help-toggle-btn" onClick={() => setOpen(o => !o)}>
        {open ? 'Hide explanation' : 'Not sure?'}
      </button>
      {open && <p className="triage-help-toggle-text">{text}</p>}
    </div>
  )
}

function OptionButton({ label, selected, onClick, type = 'single' }) {
  return (
    <button
      className={`triage-option${selected ? ' selected' : ''}`}
      onClick={onClick}
    >
      <span className={`triage-option-indicator ${type === 'multi' ? 'square' : 'round'}`}>
        {selected && (
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {label}
    </button>
  )
}

// Native dropdown — used for fields with a large option list (e.g. comunidad_autonoma)
// where a button grid is too much scroll. Mirrors the calculator's DropdownStep.
function DropdownStep({ value, onChange, options, placeholder }) {
  return (
    <select
      className="cal-select"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder || 'Select…'}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  )
}

function InlineSubQuestion({ question, field, options, value, onChange }) {
  return (
    <div className="triage-subquestion">
      <p className="triage-subquestion-label">{question}</p>
      <div className="u-stack-xs">
        {options.map(opt => (
          <button
            key={opt.value}
            className={`triage-subquestion-opt${value === opt.value ? ' selected' : ''}`}
            onClick={() => onChange(opt.value === value ? '' : opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// Combined step body — worldwide net worth + foreign accounts on one screen.
// Both fields are independently required (see isStepComplete for
// 'assets_and_net_worth' in useTriageForm.js) — neither is conditional on
// the other's answer, so this is two full questions stacked, not a
// parent/child inline reveal.
function AssetsAndNetWorthStep({ stepCfg, form }) {
  return (
    <div className="u-stack-xl">
      <div className="triage-combined-field">
        <p className="triage-combined-label">{stepCfg.netWorth.label}</p>
        {stepCfg.netWorth.hint && <p className="triage-hint">{stepCfg.netWorth.hint}</p>}
        <div className="u-stack-sm">
          {stepCfg.netWorth.options.map(opt => (
            <OptionButton
              key={opt.value}
              label={opt.label}
              selected={form.answers[stepCfg.netWorth.field] === opt.value}
              onClick={() => form.set(stepCfg.netWorth.field, opt.value)}
              type="single"
            />
          ))}
        </div>
      </div>

      <div className="triage-combined-field">
        <p className="triage-combined-label">{stepCfg.foreignAccounts.label}</p>
        {stepCfg.foreignAccounts.hint && <p className="triage-hint">{stepCfg.foreignAccounts.hint}</p>}
        <div className="u-stack-sm">
          {stepCfg.foreignAccounts.options.map(opt => (
            <OptionButton
              key={opt.value}
              label={opt.label}
              selected={form.answers[stepCfg.foreignAccounts.field] === opt.value}
              onClick={() => form.set(stepCfg.foreignAccounts.field, opt.value)}
              type="single"
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function MoveDateStep({ answers, set }) {
  const showDay = answers.move_month === '7'
  return (
    <div>
      <div className="triage-date-row">
        <div style={{ flex: 1 }}>
          <label className="triage-date-label">Month</label>
          <select
            className="triage-date-select"
            value={answers.move_month || ''}
            onChange={e => set('move_month', e.target.value)}
          >
            <option value="">Month</option>
            {MONTHS.map((m, i) => (
              <option key={m} value={String(i + 1)}>{m}</option>
            ))}
          </select>
        </div>
        <div style={{ width: '7rem' }}>
          <label className="triage-date-label">Year</label>
          <input
            type="number"
            min="2015"
            max={new Date().getFullYear() + 2}
            placeholder="2024"
            value={answers.move_year || ''}
            onChange={e => set('move_year', e.target.value)}
            className="triage-date-input"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {showDay && (
        <div style={{ marginBottom: '1rem' }}>
          <label className="triage-date-label">
            Day <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            type="number"
            min="1"
            max="31"
            placeholder="e.g. 15"
            value={answers.move_day || ''}
            onChange={e => set('move_day', e.target.value)}
            className="triage-date-input"
            style={{ width: '9rem' }}
          />
          <p className="triage-date-hint">
            If you moved in July, the exact day determines which side of the 183-day residency threshold you fall on.
          </p>
        </div>
      )}

      {/* <button
        className="triage-date-future"
        onClick={() => {
          set('move_month', '1')
          set('move_year', String(new Date().getFullYear() + 1))
          set('move_day', '')
        }}
      >
        I haven't moved yet / planning to move
      </button> */}
    </div>
  )
}

function TaxYearStep({ answers, set, taxYearOptions }) {
  if (taxYearOptions.length === 0) {
    return <p className="triage-hint" style={{ fontStyle: 'italic' }}>Select your move date first to see relevant tax years.</p>
  }
  return (
    <div className="u-stack-sm">
      {taxYearOptions.map(year => (
        <OptionButton
          key={year}
          label={String(year)}
          selected={answers.tax_year === year}
          onClick={() => set('tax_year', year)}
          type="single"
        />
      ))}
    </div>
  )
}

function ComplexityCard({ tier }) {
  const meta = COMPLEXITY_META[tier] || COMPLEXITY_META.moderate
  return (
    <div className={`triage-complexity ${meta.cls}`}>
      <div className="triage-complexity-dot" />
      <div>
        <div className="triage-complexity-tier-label">Complexity</div>
        <div className="triage-complexity-name">{meta.label}</div>
        <div className="u-text-muted-sm">{meta.desc}</div>
      </div>
    </div>
  )
}

function FlagCard({ flag, blurred = false }) {
  const meta = FLAG_META[flag.severity] || FLAG_META.info
  return (
    <div className={`triage-flag ${meta.cls}${blurred ? ' blurred' : ''}`}>
      <div className="triage-flag-header">
        <div className="triage-flag-dot" />
        <span className="triage-flag-label">{meta.label}</span>
      </div>
      <p className="u-item-title">{flag.title}</p>
      <p className="u-text-muted-sm">{flag.summary}</p>
    </div>
  )
}

// ─── EmailCaptureScreen — anonymous only ──────────────────────────────────────
// Only rendered when mode === 'anonymous'. Authenticated users never see this.

function EmailCaptureScreen({ result, onSubmitEmail, animIn }) {
  const [name,       setName]       = useState('')
  const [email,      setEmail]      = useState('')
  const [sending,    setSending]    = useState(false)
  const [sent,       setSent]       = useState(false)
  const [emailError, setEmailError] = useState(null)

  async function handleSubmit() {
    if (!name.trim())                   { setEmailError('Please enter your first name.'); return }
    const checked = validateEmail(email)
    if (!checked.ok)                    { setEmailError(checked.error); return }
    setSending(true); setEmailError(null)
    try { await onSubmitEmail(checked.email, name.trim()); setSent(true) }
    catch { setEmailError('Something went wrong. Please try again.') }
    finally { setSending(false) }
  }

  return (
    <div className={`triage-result triage-anim ${animIn ? 'in' : 'out'}`}>
      <div className="triage-result-eyebrow">Your tax situation</div>
      <h2 className="triage-result-h2">Quick snapshot</h2>
      {result.snapshot_text && <p className="u-modal-subtext">{result.snapshot_text}</p>}

      <ComplexityCard tier={result.complexity_tier} />

      {result.flags?.length > 0 && (
        <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
          <div className="triage-flags-label">Areas identified</div>
          <div className="u-stack-sm">
            {result.flags.map((flag, i) => (
              <FlagCard key={flag.id} flag={flag} blurred={i >= 1} />
            ))}
          </div>
          {result.flags.length > 1 && <div className="triage-flags-gradient" />}
        </div>
      )}

      {result.flags?.length > 1 && (
        <p className="triage-flags-more">
          + {result.flags.length - 1} more area{result.flags.length - 1 !== 1 ? 's' : ''} identified — save your snapshot to unlock
        </p>
      )}

      <div className="triage-email-card">
        {sent ? (
          <div className="triage-email-sent">
            <div className="triage-email-sent-icon">✉️</div>
            <p className="u-label-sm">Check your inbox</p>
            <p className="u-hint">We've sent a magic link to <strong>{email}</strong></p>
          </div>
        ) : (
          <>
            <p className="triage-email-prompt">
              Save your results and unlock all {result.flag_count} areas — no password needed.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <input
                type="text"
                placeholder="First name"
                value={name}
                onChange={e => { setName(e.target.value); setEmailError(null) }}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                className="app-input"
                style={{ background: 'var(--sand)' }}
                autoFocus
              />
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailError(null) }}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                className="app-input"
                style={{ background: 'var(--sand)' }}
              />
              {emailError && <p className="triage-email-error">{emailError}</p>}
              <button className="btn-primary" style={{ width: '100%', padding: '0.75rem' }} onClick={handleSubmit} disabled={sending}>
                {sending ? 'Sending…' : 'Save my snapshot →'}
              </button>
              <p className="triage-email-note">We'll send you a secure sign-in link · No password needed · Free</p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function DeferredScreen({ animIn }) {
  const [email, setEmail]     = useState('')
  const [sent, setSent]       = useState(false)
  const [sending, setSending] = useState(false)
  const [emailError, setEmailError] = useState(null)

  async function handleRemind() {
    const checked = validateEmail(email)
    if (!checked.ok) { setEmailError(checked.error); return }
    setEmailError(null)
    setSending(true)
    try {
      await supabase.auth.signInWithOtp({
        email: checked.email,
        options: { emailRedirectTo: `${window.location.origin}/spainsnapshot` },
      })
      setSent(true)
    } catch { setSent(true) }
    finally { setSending(false) }
  }

  return (
    <div className={`triage-deferred triage-anim ${animIn ? 'in' : 'out'}`}>
      <div className="triage-deferred-icon">📅</div>
      <h2 className="triage-deferred-h2">See you when you arrive</h2>
      <p className="triage-deferred-sub">
        Our tool is optimised for people already living in Spain. Leave your email and we'll send you a reminder to come back once you've moved.
      </p>
      {sent ? (
        <div className="alert-success" style={{ maxWidth: 360, margin: '0 auto' }}>
          ✓ We'll be in touch. Welcome to Spain when the time comes.
        </div>
      ) : (
        <div className="triage-deferred-form">
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={e => { setEmail(e.target.value); setEmailError(null) }}
            onKeyDown={e => e.key === 'Enter' && handleRemind()}
            className="app-input"
          />
          {emailError && <p className="triage-email-error">{emailError}</p>}
          <button
            className="btn-primary"
            style={{ padding: '0.875rem' }}
            onClick={handleRemind}
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Remind me →'}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── TriagePage ───────────────────────────────────────────────────────────────

export default function TriagePage() {
  const { user, redirectAfterSignIn } = useAuth()
  const { mode, isAuthenticated, authLoading, hubCTA } = useProductMode()
  const navigate                 = useNavigate()

  const sessionId = new URLSearchParams(window.location.search).get('session_id')

  // No more intro screen — everyone lands on the form (see file header).
  const [screen, setScreen]         = useState('form')
  const [animIn, setAnimIn]         = useState(true)
  const [checking, setChecking]     = useState(true)
  const [partner,    setPartner]    = useState(null)

  // BUG FIX: this used to re-parse `?ref=` directly from
  // window.location.search — meaning "with {partner}" only ever showed
  // when the referral param was literally present in THIS page's current
  // URL. If the referral was captured earlier (a different page, an
  // earlier visit within the cookie's 60-day window — see
  // src/lib/partnerAttribution.js), this silently stayed null even though
  // the actual attribution (cookie + the session's own partner_ref) was
  // intact. CalculatorPage.js was already fixed for exactly this; this
  // page never got the same fix, which is why its pill was missing the
  // "with {partner}" suffix when the other tool pages' weren't. Same
  // duplication bug partner_referral_framework.md's own self-check warns
  // about re-introducing — getActivePartnerRef() is the one read point,
  // never re-parse location.search directly.
  const partnerRef = getActivePartnerRef()

  // Disambiguates "lookup still pending" from "lookup resolved, no matching
  // partner row" (a garbage/typo'd ?ref=). Flips true once the .then() below
  // fires, whether or not a row was found — see ticket 014-partnerintro D6.
  const [partnerChecked, setPartnerChecked] = useState(false)

  useEffect(() => {
    if (!partnerRef) return
    fetchPartnerNameByRef(partnerRef)
      .then(({ data }) => {
        if (data) setPartner(data)
        setPartnerChecked(true)
      })
  }, [partnerRef])

  // ── Partner-referral intro screen (ticket 014-partnerintro) ────────────
  // likelyPartnerLinked is used only to decide whether to show the loading
  // spinner below — never for attribution itself, which stays exclusively
  // cookie-based via partnerRef once authLoading is false.
  const likelyPartnerLinked = !!(
    partnerRef || new URLSearchParams(window.location.search).get('ref')
  )
  const [showPartnerIntro, setShowPartnerIntro] = useState(null) // null = undecided

  useEffect(() => {
    if (authLoading) return
    if (!partnerRef) { setShowPartnerIntro(false); return }
    if (isAuthenticated) { setShowPartnerIntro(false); return }
    if (!partnerChecked) return // still waiting on the partner-name fetch — keep the spinner up
    // partnerChecked is true now. Either a real partner resolved, or the
    // ref was garbage/unrecognized (partner still null) — in that case,
    // fall straight through to the real first question, same as if
    // partnerRef had been absent, rather than showing a broken/placeholder
    // sentence.
    setShowPartnerIntro(!!partner)
  }, [authLoading, partnerRef, isAuthenticated, partnerChecked, partner])

  const form            = useTriageForm()
  const claimFired      = useRef(false)
  const triageChecked   = useRef(false)

  // Legacy ?session_id= links: send signed-in visitors to /intelligence
  useEffect(() => {
    if (!sessionId || !user || claimFired.current) return
    claimFired.current = true
    // The old anon-key triage-claim call here could never succeed (no user
    // token, no public_token) and was removed in 049; only the redirect stays.
    navigate('/intelligence', { replace: true })
  }, [user, sessionId, navigate])

  useEffect(() => {
    if (!user) { setChecking(false); return }
    if (triageChecked.current) return
    triageChecked.current = true
    supabase
      .from('tax_year_profiles')
      .select('triage_completed')
      .eq('user_id', user.id)
      .eq('triage_completed', true)
      .limit(1)
      .then(() => setChecking(false))
  }, [user])

  // React to form result
  useEffect(() => {
    if (!form.result) return

    if (form.result.outcome === 'deferred') {
      transition('deferred')
      return
    }

    if (form.result.outcome === 'complete' && form.result.public_token) {
      if (mode === 'authenticated') {
        // Auto-claim: fire claim-session (fire-and-forget) then navigate to
        // results. If it fails, TriageResults shows a manual "Save to my
        // account" button.
        supabase.auth.getSession().then(({ data: { session: authSession } }) => {
          if (authSession) {
            claimSession({
              product:     'triage',
              publicToken: form.result.public_token,
              accessToken: authSession.access_token,
            })
          }
        })
      }
      navigate(`/spainsnapshot/results/${form.result.public_token}`, { replace: true })
    }
  }, [form.result])

  function transition(next) {
    setAnimIn(false)
    setTimeout(() => { setScreen(next); setAnimIn(true) }, 180)
  }

  function stepTransition(fn) {
    setAnimIn(false)
    setTimeout(() => { fn(); setAnimIn(true) }, 180)
  }

  function handleNext() {
    if (form.isLastStep) form.submit({ source: 'homepage' })
    else stepTransition(() => form.goNext())
  }

  function handleBack() {
    // No intro to fall back to at index 0 anymore — the "← Back" button
    // is now hidden in that case for everyone (see its visibility
    // condition below), matching CalculatorPage.js's Step1 pattern.
    // AnonHeader's wordmark is the "back to start" affordance instead.
    if (form.currentStepIndex > 0) stepTransition(() => form.goBack())
  }

  async function handleEmailSubmit(email, name) {
    if (form.result?.public_token) {
      fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/triage-save-email`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_token: form.result.public_token, email, display_name: name }),
      }).catch(() => {})
    }
    await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback?triage_session_id=${form.result?.public_token}`,
      },
    })
  }

  const stepCfg = screen === 'form' ? STEP_CONFIG[form.currentStep] : null

  if (checking) {
    return (
      <div className="asmt-state">
        <div className="app-spinner" />
      </div>
    )
  }

  // Loading gate — only for visitors who might be partner-referred (see
  // likelyPartnerLinked above). Organic/non-referred visitors never hit
  // this and see zero behavior change. (This page's own `checking` gate
  // above is a separate, pre-existing, unrelated bug — keyed off `!user`
  // rather than `authLoading` — not fixed here; it just doesn't already
  // solve this ticket's timing race, which is why this second gate exists.)
  if (likelyPartnerLinked && showPartnerIntro === null) {
    return (
      <div className="asmt-state">
        <div className="app-spinner" />
      </div>
    )
  }

  return (
    <div className="triage-root">

      {/* Progress bar — form only */}
      {screen === 'form' && showPartnerIntro !== true && (
        <div className="triage-progress-track">
          <div className="triage-progress-fill" style={{ width: `${form.progressPct}%` }} />
        </div>
      )}

      {/* ── Form / email / deferred — narrow column ── */}
      <div
        className="triage-col"
        style={{
          opacity:    animIn ? 1 : 0,
          transform:  animIn ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 200ms ease, transform 200ms ease',
        }}
      >

        {/* Persistent for every anonymous view of this page now (was
            previously only reachable via the deleted intro screen).
            Renders ProductTag inline next to the wordmark itself now —
            see AnonHeader's own comment in HomeLink.js. */}
        <AnonHeader show={mode === 'anonymous'} product="triage" partnerName={partner?.name} onSignInSuccess={redirectAfterSignIn} />

          {/* ── Partner-referral intro (ticket 014-partnerintro), or Form ── */}
          {showPartnerIntro ? (
            <PartnerIntroScreen
              partnerName={partner?.name}
              onContinue={() => setShowPartnerIntro(false)}
            />
          ) : (
          screen === 'form' && stepCfg && (
            <div className="triage-form">
              <div className="triage-form-meta">
                <span className="triage-step-label">
                  Question {form.currentStepIndex + 1} of {form.totalSteps}
                </span>
                {form.currentStepIndex > 0 && (
                  <button className="triage-back-link" onClick={handleBack}>← Back</button>
                )}
              </div>

              <h2 className="triage-question">{stepCfg.question}</h2>

              {stepCfg.hint
                ? <p className="triage-hint">{stepCfg.hint}</p>
                : stepCfg.type === 'multi_select' && <p className="triage-hint" style={{ fontStyle: 'italic' }}>Select all that apply</p>
              }

              {stepCfg.type === 'move_date' && (
                <MoveDateStep answers={form.answers} set={form.set} />
              )}
              {stepCfg.type === 'tax_year' && (
                <TaxYearStep answers={form.answers} set={form.set} taxYearOptions={form.taxYearOptions} />
              )}
              {stepCfg.type === 'assets_and_net_worth' && (
                <AssetsAndNetWorthStep stepCfg={stepCfg} form={form} />
              )}
              {stepCfg.type === 'single_select' && stepCfg.renderAs === 'dropdown' && (
                <DropdownStep
                  value={form.answers[stepCfg.field]}
                  onChange={v => form.set(stepCfg.field, v)}
                  options={stepCfg.options}
                  placeholder={stepCfg.dropdownPlaceholder}
                />
              )}
              {stepCfg.type === 'single_select' && stepCfg.renderAs !== 'dropdown' && (
                <div className="u-stack-sm">
                  {stepCfg.options.map(opt => {
                    const isSelected = form.answers[stepCfg.field] === opt.value
                    return (
                      <React.Fragment key={opt.value}>
                        <OptionButton
                          label={opt.label}
                          selected={isSelected}
                          onClick={() => form.set(stepCfg.field, opt.value)}
                          type="single"
                        />
                        {isSelected && stepCfg.field === 'citizenship_status' &&
                          opt.value === 'eu_citizen' && (
                          <InlineSubQuestion
                            question="Do you also hold US citizenship or a Green Card?"
                            field="eu_citizen"
                            options={[
                              { value: true,  label: 'Yes — I also have US citizenship or a Green Card' },
                              { value: false, label: 'No — EU citizenship only' },
                            ]}
                            value={form.answers.eu_citizen}
                            onChange={v => form.set('eu_citizen', v)}
                          />
                        )}
                        {isSelected && stepCfg.field === 'marital_status' &&
                          (opt.value === 'married' || opt.value === 'civil_partnership') && (
                          <InlineSubQuestion
                            question="How are your assets broadly split between you and your partner?"
                            field="asset_split"
                            options={getSlotOptions('asset_split')}
                            value={form.answers.asset_split || ''}
                            onChange={v => form.set('asset_split', v)}
                          />
                        )}
                        {isSelected && stepCfg.field === 'filed_spain_before' &&
                          opt.value === true && (
                          <InlineSubQuestion
                            question="Do you currently work with a gestor or Spanish tax advisor?"
                            field="has_gestor"
                            options={getSlotOptions('has_gestor')}
                            value={form.answers.has_gestor}
                            onChange={v => form.set('has_gestor', v)}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
              {stepCfg.type === 'single_select' && stepCfg.renderAs !== 'dropdown' && stepCfg.helpText && (
                <HelpToggle text={stepCfg.helpText} />
              )}
              {stepCfg.type === 'multi_select' && (
                <div className="u-stack-sm">
                  {stepCfg.options.map(opt => {
                    const isSelected = (form.answers[stepCfg.field] || []).includes(opt.value)
                    return (
                      <React.Fragment key={opt.value}>
                        <OptionButton
                          label={opt.label}
                          selected={isSelected}
                          onClick={() => form.toggleMulti(stepCfg.field, opt.value, stepCfg.noneValue)}
                          type="multi"
                        />
                        {isSelected && stepCfg.field === 'income_types' && opt.value === 'employment_us' && (
                          <InlineSubQuestion
                            question="Where was this employment income earned?"
                            field="employment_location_when_earned"
                            options={getSlotOptions('employment_location_when_earned')}
                            value={form.answers.employment_location_when_earned || ''}
                            onChange={v => form.set('employment_location_when_earned', v)}
                          />
                        )}
                        {isSelected && stepCfg.field === 'income_types' && opt.value === 'self_employment' && (
                          <InlineSubQuestion
                            question="Where is your self-employment activity based?"
                            field="self_employment_country"
                            options={getSlotOptions('self_employment_country')}
                            value={form.answers.self_employment_country || ''}
                            onChange={v => form.set('self_employment_country', v)}
                          />
                        )}
                        {isSelected && stepCfg.field === 'income_types' && opt.value === 'business_partnership' && (
                          <InlineSubQuestion
                            question="Is this from a US pass-through entity, or a foreign company?"
                            field="business_partnership_type"
                            options={getSlotOptions('business_partnership_type')}
                            value={form.answers.business_partnership_type || ''}
                            onChange={v => form.set('business_partnership_type', v)}
                          />
                        )}
                        {isSelected && stepCfg.field === 'income_types' && opt.value === 'rental' && (
                          <InlineSubQuestion
                            question="Where is the rental property located?"
                            field="rental_location"
                            options={getSlotOptions('rental_property_location')}
                            value={form.answers.rental_location || ''}
                            onChange={v => form.set('rental_location', v)}
                          />
                        )}
                        {isSelected && stepCfg.field === 'income_types' && opt.value === 'pension' && (
                          <>
                            <InlineSubQuestion
                              question="Where is your pension or retirement income from?"
                              field="pension_source"
                              options={getSlotOptions('pension_source')}
                              value={form.answers.pension_source || ''}
                              onChange={v => form.set('pension_source', v)}
                            />
                            {['us', 'both', 'other'].includes(form.answers.pension_source) && (
                              <InlineSubQuestion
                                question="Is this a government or civil-service pension (federal, state, or military), or private-sector?"
                                field="pension_is_government"
                                options={getSlotOptions('pension_is_government')}
                                value={form.answers.pension_is_government}
                                onChange={v => form.set('pension_is_government', v)}
                              />
                            )}
                          </>
                        )}
                        {isSelected && stepCfg.field === 'major_events' && opt.value === 'sold_property' && (
                          <InlineSubQuestion
                            question="Where was the property located?"
                            field="property_sale_location"
                            options={getSlotOptions('property_sale_location')}
                            value={form.answers.property_sale_location || ''}
                            onChange={v => form.set('property_sale_location', v)}
                          />
                        )}
                        {isSelected && stepCfg.field === 'major_events' && opt.value === 'retirement_withdrawal' && (
                          <InlineSubQuestion
                            question="What type of account was this from?"
                            field="retirement_withdrawal_type"
                            options={getSlotOptions('retirement_withdrawal_type')}
                            value={form.answers.retirement_withdrawal_type || ''}
                            onChange={v => form.set('retirement_withdrawal_type', v)}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
              {stepCfg.type === 'textarea' && (
                <textarea
                  className="app-input triage-notes-input"
                  placeholder="e.g. I recently sold a house in the US, I'm not sure if I need to register as autónomo, I have a pending Beckham application..."
                  value={form.answers[stepCfg.field] || ''}
                  onChange={e => form.set(stepCfg.field, e.target.value)}
                  rows={5}
                />
              )}

              {form.submitError && (
                <div className="triage-submit-error">{form.submitError}</div>
              )}

              <button
                className="triage-next"
                onClick={handleNext}
                disabled={!form.canProceed || form.submitting}
              >
                {form.submitting ? 'Analysing…' : form.isLastStep ? 'See my snapshot →' : 'Continue →'}
              </button>
            </div>
          ))}

          {/* ── Email capture — anonymous only ── */}
          {screen === 'email' && form.result && mode === 'anonymous' && (
            <EmailCaptureScreen result={form.result} onSubmitEmail={handleEmailSubmit} animIn={animIn} />
          )}

          {/* ── Deferred ── */}
          {screen === 'deferred' && (
            <DeferredScreen animIn={animIn} />
          )}

        </div>
    </div>
  )
}